import { useCallback, useEffect, useId, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetDay, GetCruise, GetShip, GetTrip } from "../../App/Context"
import { computeEditHistory } from "../../App/EditHistory"
import { compositeField, dateValue, referenceDate, FieldFormat } from "../../App/Format"
import { Ship } from "../../App/Ship"
import { useEditForm } from "../../App/useEditForm"
import { Block, BlockContainer, BlockField, BlockRow } from "../../Components/Block"
import { EditGroup, EditGroupFieldDefinitions, saveErrorMessage } from "../../Components/EditGroup"
import { EditHistory } from "../../Components/EditHistory"
import { Icon, Icons } from "../../Components/Icon"
import { AutocompleteInput } from "../../Components/Input"
import { Select, SelectOption } from "../../Components/Select"
import { ShipUserSelect, ShipUserSelectData } from "../../Components/ShipUserSelect"
import { IAuditLogEditSource, ICruiseCalculations, IDay, IDayResponse, IEditPermissivenessLevel, IShipAutocompleteResponse, IShipUser, IStatus, IStatusType, IShipStatus, IShipUserCrewRole, ApiClient, DayApi, ShipApi } from "../../Types"
import { DayViewData } from "./Context"
import { PreviousDayProps } from "./Params"
import { GetShipUsers, GetShipView } from "../ShipView"
import { showsEditHistory } from "../Log/editability"
import { dayShipStatusSummary } from "../Log/status"

// Live current value for a `DayEditableField`, used by `computeEditHistory` (issue
// #529) to resolve the "new value" of each field's most recent edit -- every field
// maps 1:1 to a `Day` property.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
function currentDayFieldValue(day: any, field: string): unknown {
  switch (field) {
    case "comment": case "crew": case "endLocation": case "passengers":
    case "skipper": case "startLocation":
      return day[field];
    default:
      return undefined;
  }
}

export const DayEditViewData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  updateDay(api: ApiClient, shipId: number, dayId: number, values: any): Promise<IDayResponse> {
    return new DayApi(api).days.update({ params: { shipId, dayId }, body: values }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  createDay(api: ApiClient, shipId: number, tripId: number, values: any): Promise<IDayResponse> {
    return new DayApi(api).days.create({ params: { shipId, tripId }, body: values }).call();
  },
  loadLocationAutocomplete(api: ApiClient, shipId: number): Promise<IShipAutocompleteResponse> {
    return new ShipApi(api).ships.autocomplete({ params: { shipId, field: "location" } }).call();
  },
  loadDayCommentAutocomplete(api: ApiClient, shipId: number): Promise<IShipAutocompleteResponse> {
    return new ShipApi(api).ships.autocomplete({ params: { shipId, field: "dayComment" } }).call();
  },

  // #1137: the most recent persisted ("real") day strictly before `day` within the
  // trip's day list -- the source for a blank/backfilled day's carried-forward
  // status. `undefined` when there is no earlier real day (e.g. the trip's very
  // first day has zero logs).
  previousRealDay(days: IDay[], day: IDay): IDay | undefined {
    return days.filter((d) => d.id > 0 && d.day < day.day).sort((a, b) => b.day - a.day)[0];
  },

  // #1137: the ship's resting status to carry into a blank day -- its most recent
  // preceding real day's last recorded ship status (statuses are stored
  // chronologically), or `.none` if that day itself recorded no ship statuses.
  carriedForwardStatus(shipStatuses: IStatus[] | undefined): IShipStatus {
    const retained = shipStatuses?.filter((status) => status.deletedAt == undefined);
    const lastStatus = retained?.[retained.length - 1];
    return lastStatus ? (lastStatus.value as IShipStatus) : IShipStatus.none;
  },

  /**
   * Converts an absolute `Date` field from the stored calculation (typegen'd as an
   * ISO8601 string, e.g. `calculated.sunrise`/`.sunset`) into the ship-local clock time
   * — the numeric "seconds since the reference date" value the `"timeUtc"` format
   * expects (issue #67).
   */
  sunTimeValue(iso: string | undefined, timezone: number): number | undefined {
    if (!iso) {
      return undefined;
    }
    return (dateValue(iso).valueOf() - referenceDate) / 1000 + timezone;
  },

  // Per-field value formatters for the Day fields that carry a raw shipUserId (issue
  // #529): `skipper`/`crew`/`passengers` resolve to the matching ship user's display
  // name via `ShipUserSelectData.userLabel` -- the exact convention `ShipUserSelect`
  // itself uses -- so `EditHistory`'s previous/new values show a name, not a bare id.
  // `comment`/`startLocation`/`endLocation` have no entry: they're plain text, already
  // correctly shown as-is by `EditHistory`'s generic fallback.
  fieldFormat(shipUsers: IShipUser[] | undefined): FieldFormat {
    const userLabel = compositeField(
      (value): value is number => typeof value === "number",
      (id): string => {
        const user = shipUsers?.find((u) => u.id == id);
        return user ? ShipUserSelectData.userLabel(user) : "–";
      },
    );
    const userList = compositeField(
      (value): value is unknown[] => Array.isArray(value),
      (ids): string => (ids.length > 0 ? ids.map((id) => userLabel(id)).join(", ") : "–"),
    );
    return {
      crew: userList,
      passengers: userList,
      skipper: userLabel,
    };
  },
};

interface DayEditViewProps {
  day: IDay;
  editable?: boolean;
  isCurrentDay?: boolean;
  isFirstTripDay?: boolean;
  isTomorrow?: boolean;
  previousDay?: PreviousDayProps;
  // Opens the "Edit history" section (issue #529) pre-expanded — for catalog/demo
  // fixtures only; production always starts it collapsed.
  initEditHistoryOpen?: boolean;
  // Seeds the save-error state (issue #529) — for catalog/demo fixtures only;
  // production always starts with no error, only ever setting one via a real
  // rejected `onSave`.
  initSaveError?: unknown;
}

export function DayEditView({ day, editable, isCurrentDay, isFirstTripDay: _isFirstTripDay, isTomorrow, initEditHistoryOpen, initSaveError, previousDay }: DayEditViewProps) {
  const startLocationId = useId();
  const endLocationId = useId();
  const app = GetApp()!;
  const t = app.getT("DayEditView");
  const { auditLogEdits, logs, recordAuditLogEdits, recordSavedDay, statuses, updateDay, createDay } = GetDay();
  const { trip, days } = GetTrip();
  const { cruise } = GetCruise();
  const shipView = GetShipView();
  const shipUsers = GetShipUsers();
  const [autocompleteLocations, setAutocompleteLocations] = useState<string[] | undefined>(undefined);
  const _currentDay = app.current?.day && day.day == app.current?.day;
  const [autocompleteDayComment, setAutocompleteDayComment] = useState<string[] | undefined>(undefined);
  const cruiseCalculated = cruise?.calculated as ICruiseCalculations;

  // #1137: a genuinely blank/backfilled day -- no Day row exists, and it's neither
  // today's not-yet-created bootstrap day nor tomorrow's placeholder (both of which
  // also start with `!day.id` but keep today's existing editable behavior). The Day
  // tab is fully read-only for a blank day, stronger than plain `editable={false}`,
  // since `alwaysEditable` would otherwise still let comment/location fields through.
  const blank = !day.id && !isCurrentDay && !isTomorrow;

  const alwaysEditableFields: EditGroupFieldDefinitions[] = ["comment", { name: "endLocation", type: "string", default: "" }, { name: "startLocation", type: "string", default: "" }];
  const limitedEditableFields: EditGroupFieldDefinitions[] = [{ name: "crew", array: true, type: "int" }, { name: "day", type: "int" }, { name: "passengers", array: true, type: "int" }, { name: "skipper", type: "int" }];
  const editableFields = [...alwaysEditableFields, ...limitedEditableFields];
  const formEditable = !blank && app.editable && editable && (isCurrentDay || isTomorrow);
  const alwaysEditable = !blank && (app.editable || shipView.shipCloudEditable);

  const { edited, onEdited, saveEdits, saveError, onSaveError } = useEditForm({
    isNew: !day.id,
    initSaveError,
    update: async (values) => {
      const result = await updateDay(values);
      if (result?.auditLogEdits) {
        recordAuditLogEdits(result.auditLogEdits);
      }
      // Issue #1136: `day` itself is owned above this view and only refreshes on the
      // next live broadcast / full day load, so the response's authoritative row is
      // kept here too -- otherwise the edit history below resolves the field just
      // saved from the pre-edit snapshot. Only read for that resolution; the form's
      // own (uncontrolled) inputs keep what the user typed.
      if (result?.day) {
        recordSavedDay?.(result.day);
      }
      return result;
    },
    create: (values) => createDay(values),
  });

  // `DayEditableField`s are all `.always` (`allowsDayFieldEdit` is unconditionally
  // `true` -- Day has no NMEA-capable fields), so `days.update` never actually
  // rejects a field; this only ever surfaces the generic fallback (network/other
  // failures), not a field-specific rejection message.
  const saveErrorText = useMemo(() => saveErrorMessage(saveError, {}, {
    fields: () => t("save_error_generic"),
    generic: t("save_error_generic"),
  }), [saveError, t]);

  // Edit history (issue #529): this day's own `AuditLogEdit` rows (`sourceType ==
  // day`, matched via `sourceId`) out of `DayContext.auditLogEdits` -- a flat list
  // shared with the Log/Status edit-history data, since `AuditLogEdit` isn't
  // `sourceId`-namespaced per record kind on its own.
  const dayAuditLogEdits = useMemo(() => {
    return auditLogEdits.filter((e) => e.sourceType == IAuditLogEditSource.day && e.sourceId == day.id);
  }, [auditLogEdits, day.id]);
  const editHistory = useMemo(() => {
    return computeEditHistory(dayAuditLogEdits, (field) => currentDayFieldValue(day, field));
  }, [dayAuditLogEdits, day]);
  const lastEditor = useMemo(() => {
    if (editHistory.length < 1) {
      return undefined;
    }
    return shipUsers?.find((u) => u.id == editHistory[0].shipUserId);
  }, [editHistory, shipUsers]);
  // Every `DayEditableField` already has its own single-field translation key
  // (unlike Log, which needed 2 new ones) -- just map the field name to its
  // existing snake_case key.
  const fieldLabels: Record<string, string> = {
    comment: t("comment"),
    crew: t("crew"),
    endLocation: t("end_location"),
    passengers: t("passengers"),
    skipper: t("skipper"),
    startLocation: t("start_location"),
  };
  const fieldLabel = (field: string) => fieldLabels[field] ?? field;
  const editHistoryFieldFormat = useMemo(() => DayEditViewData.fieldFormat(shipUsers), [shipUsers]);
  // Edit History visibility (issue #1169): a code-level policy, not a ship
  // setting -- `.unrestricted` already discards the field-level protections that
  // give the audit trail its evidentiary value, so the table (but not the "last
  // edited" line itself) is hidden at that level. See `Log/editability.ts`.
  const ship = GetShip();
  const editPermissivenessLevel = Ship.shipSettings(ship).editPermissiveness ?? IEditPermissivenessLevel.strict;
  const editHistoryVisible = showsEditHistory(editPermissivenessLevel);
  const [editHistoryOpen, setEditHistoryOpen] = useState(initEditHistoryOpen ?? false);

  useEffect(() => {
    const load = async () => {
      if (!autocompleteLocations) {
        const locationData = await DayEditViewData.loadLocationAutocomplete(app.apiClient, day.shipId);
        setAutocompleteLocations(locationData?.values || []);
      }

      if (!autocompleteDayComment) {
        const dayCommentData = await DayEditViewData.loadDayCommentAutocomplete(app.apiClient, day.shipId);
        setAutocompleteDayComment(dayCommentData?.values || []);
      }
    };
    load().catch(console.error);
  }, [day, autocompleteLocations, autocompleteDayComment]);

  const dayStatusOptions: SelectOption[] = useMemo(() => {
    return [];
  }, [t]);

  // #1137: a blank day has no logs of its own, so it never has its own statuses --
  // fetch the most recent preceding real day's instead, so the carried-forward
  // status computed below has something to carry forward from.
  const previousRealDay = useMemo(() => {
    return blank ? DayEditViewData.previousRealDay(days, day) : undefined;
  }, [days, day, blank]);
  const [previousDayStatuses, setPreviousDayStatuses] = useState<IStatus[] | undefined>(undefined);
  useEffect(() => {
    if (!previousRealDay) {
      setPreviousDayStatuses(undefined);
      return;
    }
    const load = async () => {
      const data = await DayViewData.loadLogs(app.apiClient, previousRealDay.shipId, previousRealDay.id);
      setPreviousDayStatuses(data.statuses);
    };
    load().catch(console.error);
  }, [previousRealDay]);

  const shipStatuses = useMemo(() => {
    const source = blank ? previousDayStatuses : statuses;
    return source?.filter((s) => s.statusType == IStatusType.ship && s.deletedAt == undefined);
  }, [statuses, previousDayStatuses, blank, day]);
  const statusButton = useCallback((status: IShipStatus) => {
    const statusName = IShipStatus[status];
    if (status == IShipStatus.none) {
      return <>{app.t(`LogNavigationView_status_${statusName}`)}</>;
    }
    return <><Icon className="DayEditView-status-icon" icon={`status-${statusName}`} /> {app.t(`LogNavigationView_status_${statusName}`)}</>;
  }, []);
  const { dayStatus, overnightStatus } = useMemo(() => {
    // #1137: a blank day shows the status carried forward from the closest
    // preceding real day, rather than deriving anything from its own (nonexistent)
    // statuses.
    if (blank) {
      const carried = DayEditViewData.carriedForwardStatus(shipStatuses);
      return { dayStatus: carried, overnightStatus: carried };
    }
    return dayShipStatusSummary(day, statuses, logs);
  }, [day, statuses, shipStatuses, logs, blank]);
  const dayStatusButton = useMemo(() => {
    return statusButton(dayStatus);
  }, [dayStatus]);
  const overnightStatusButton = useMemo(() => {
    return statusButton(overnightStatus);
  }, [overnightStatus]);
  const crewPriorityIds = useMemo(() => {
    if (cruiseCalculated) {
      return [...(cruiseCalculated.crew || []), ...(cruiseCalculated.skippers || [])];
    }
    return undefined;
  }, [cruise]);

  const persistedSkipper = useMemo(() => {
    return day.skipper || previousDay?.skipper;
  }, [day, previousDay]);

  // The skipper the user has picked in this form but not saved yet. The crew field
  // pins the skipper as a fixed value, and reading only the persisted day left a
  // freshly selected skipper out of the crew list until an unrelated change forced a
  // save (#1213).
  const [selectedSkipper, setSelectedSkipper] = useState<number | undefined>(undefined);
  useEffect(() => {
    setSelectedSkipper(undefined);
  }, [persistedSkipper]);
  const skipper = selectedSkipper ?? persistedSkipper;

  const selectSkipper = useCallback((value?: number | number[]) => {
    setSelectedSkipper(typeof value === "number" ? value : undefined);
  }, []);

  const timezone = useMemo(() => {
    if (isCurrentDay && logs.length < 1 && app.status) {
      return app.status.systemTimezone;
    } else if (!isCurrentDay && day.day == 0) {
      return trip.timezone;
    }
    return day.timezone;
  }, [day, logs, trip, isCurrentDay, app.status]);

  const sunrise = useMemo(() => DayEditViewData.sunTimeValue(day.calculated?.sunrise, day.timezone), [day]);
  const sunset = useMemo(() => DayEditViewData.sunTimeValue(day.calculated?.sunset, day.timezone), [day]);

  return (
    <BlockContainer className="DayEditView">
      <div className="DayStatsView-block-time block">
        <EditGroup buttonFirst={true} changed={edited} editable={formEditable || alwaysEditable} editableFields={formEditable ? editableFields : alwaysEditableFields} error={saveErrorText} onSave={saveEdits} onSaveError={onSaveError}>
          {!day.id && <input type="hidden" name="day" value={day.day} />}
          <BlockRow>
            <BlockField label={t("status_day")} className="block_field_small_12">
              <Select button={dayStatusButton} name="dayStatus" value={dayStatus} options={dayStatusOptions} readOnly={true} onSelect={(_value?: string) => {}} />
            </BlockField>
            {sunrise != undefined && (
              <BlockField label={t("sunrise")} className="DayEditView-sunrise block_field_small_10">
                <div className="input">{app.format("timeUtc", sunrise)}</div>
              </BlockField>
            )}
            {sunset != undefined && (
              <BlockField label={t("sunset")} className="DayEditView-sunset block_field_small_10">
                <div className="input">{app.format("timeUtc", sunset)}</div>
              </BlockField>
            )}
            <BlockField label={t("status_overnight")}className="block_field_small_12">
              <Select button={overnightStatusButton} name="dayStatus" value={dayStatus} options={dayStatusOptions} readOnly={true} onSelect={(_value?: string) => {}} />
            </BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField label={t("start_location")} labelFor={startLocationId}>
              {!formEditable && !alwaysEditable ? (
                <input id={startLocationId} value={(day.startLocation || "").length > 0 ? day.startLocation : previousDay?.startLocation} name="startLocation" onChange={onEdited} readOnly={true} />
              ) : (
                <AutocompleteInput id={startLocationId} defaultValue={day.startLocation} name="startLocation" onChange={onEdited} placeholder={previousDay?.startLocation} readOnly={false} suggestions={autocompleteLocations} />
              )}
            </BlockField>
            <BlockField label={t("end_location")} labelFor={endLocationId}>
              <AutocompleteInput id={endLocationId} defaultValue={day.endLocation} name="endLocation" onChange={onEdited} readOnly={!formEditable && !alwaysEditable} suggestions={autocompleteLocations} />
            </BlockField>
            <BlockField label={t("timezone")} className="block_field_small_10 ">
              <div className="input">{app.format("timezone", timezone)}</div>
            </BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField label={t("skipper")} className="block_field_small_10">
              <ShipUserSelect key={persistedSkipper} name="skipper" defaultValue={persistedSkipper} onChange={onEdited} onSelectionChange={selectSkipper} priorityIds={cruiseCalculated?.skippers} role={IShipUserCrewRole.skipper} readOnly={!formEditable} />
            </BlockField>
            <BlockField label={t("crew")} className="crew-select">
              <ShipUserSelect multi={true} defaultValue={day.crew || previousDay?.crew} includeValues={skipper ? [skipper] : undefined} onChange={onEdited} priorityIds={crewPriorityIds} role={IShipUserCrewRole.crew} name="crew" readOnly={!formEditable} />
            </BlockField>
          </BlockRow>
          <BlockRow className="DayView-passenger-row">
            <BlockField label={t("passengers")}>
              <ShipUserSelect multi={true} defaultValue={day.passengers || previousDay?.passengers} onChange={onEdited} role={IShipUserCrewRole.none} name="passengers" readOnly={!formEditable} />
            </BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField label={t("comment")}>
              <AutocompleteInput defaultValue={day.comment} name="comment" onChange={onEdited} readOnly={!formEditable && !alwaysEditable} suggestions={autocompleteDayComment} />

            </BlockField>
          </BlockRow>
        </EditGroup>
      </div>
      {editHistory.length > 0 && (
        <Block className="day-details">
          <BlockRow>
            <BlockField
              className="day-details-last-edited"
              onClick={editHistoryVisible ? () => setEditHistoryOpen((open) => !open) : undefined}
            >
              {lastEditor
                ? t("details_last_edited_by", { name: lastEditor.name || lastEditor.shortname || "" })
                : t("details_last_edited")}
              {editHistoryVisible && <Icon icon={editHistoryOpen ? Icons.ArrowUp : Icons.ArrowDown} />}
            </BlockField>
          </BlockRow>
        </Block>
      )}
      {editHistoryVisible && (
        <EditHistory entries={editHistory} fieldLabel={fieldLabel} fieldFormat={editHistoryFieldFormat} open={editHistoryOpen} />
      )}
    </BlockContainer>
  );
}
